// Cart 前端狀態機（純函數，UI 控制器使用）
// see architecture.md#L1050
//
// idle → adding → reviewing → checkout → submitting → ok → idle
//                                  │          │
//                                  │          └── offline? → outbox → retry
//                                  └── cancel → reviewing

export const CART_STATES = Object.freeze({
  IDLE: 'idle',
  ADDING: 'adding',
  REVIEWING: 'reviewing',
  CHECKOUT: 'checkout',
  SUBMITTING: 'submitting',
  OK: 'ok',
  OUTBOX: 'outbox',
});

const TRANSITIONS = {
  [CART_STATES.IDLE]: [CART_STATES.ADDING],
  [CART_STATES.ADDING]: [CART_STATES.ADDING, CART_STATES.REVIEWING, CART_STATES.IDLE],
  [CART_STATES.REVIEWING]: [CART_STATES.ADDING, CART_STATES.CHECKOUT, CART_STATES.IDLE],
  [CART_STATES.CHECKOUT]: [CART_STATES.REVIEWING, CART_STATES.SUBMITTING],
  [CART_STATES.SUBMITTING]: [CART_STATES.OK, CART_STATES.OUTBOX, CART_STATES.CHECKOUT],
  [CART_STATES.OK]: [CART_STATES.IDLE],
  [CART_STATES.OUTBOX]: [CART_STATES.SUBMITTING, CART_STATES.CHECKOUT],
};

export function canTransition(from, to) {
  return TRANSITIONS[from]?.includes(to) ?? false;
}

export function nextStates(from) {
  return TRANSITIONS[from] ?? [];
}

export function isTerminal(state) {
  return TRANSITIONS[state]?.length === 0;
}

/**
 * 事件導向 transition：根據動作 + 當前 context 計算下一狀態
 * @param {string} current
 * @param {string} event - 'add_item' | 'review' | 'checkout' | 'submit' | 'success' | 'network_error' | 'retry' | 'cancel' | 'reset'
 * @param {Object} ctx - { itemCount, online }
 * @returns {string} 下一狀態
 */
export function reduce(current, event, ctx = {}) {
  const { itemCount = 0, online = true } = ctx;

  switch (event) {
    case 'add_item':
      if (current === CART_STATES.IDLE) return CART_STATES.ADDING;
      if (current === CART_STATES.ADDING || current === CART_STATES.REVIEWING) return CART_STATES.ADDING;
      return current;

    case 'review':
      if (current === CART_STATES.ADDING && itemCount > 0) return CART_STATES.REVIEWING;
      return current;

    case 'checkout':
      if (current === CART_STATES.REVIEWING && itemCount > 0) return CART_STATES.CHECKOUT;
      return current;

    case 'submit':
      if (current === CART_STATES.CHECKOUT) return CART_STATES.SUBMITTING;
      return current;

    case 'success':
      if (current === CART_STATES.SUBMITTING) return CART_STATES.OK;
      return current;

    case 'network_error':
      if (current === CART_STATES.SUBMITTING) {
        return online ? CART_STATES.CHECKOUT : CART_STATES.OUTBOX;
      }
      return current;

    case 'retry':
      if (current === CART_STATES.OUTBOX) return CART_STATES.SUBMITTING;
      return current;

    case 'cancel':
      if (current === CART_STATES.CHECKOUT) return CART_STATES.REVIEWING;
      if (current === CART_STATES.ADDING && itemCount === 0) return CART_STATES.IDLE;
      return current;

    case 'reset':
      return CART_STATES.IDLE;

    default:
      return current;
  }
}
